const number=value=>Number(value)||0;

export function entityRole(entity={}) {
  return entity.role||'cut';
}

export function arcPoints(entity,maximumStepDegrees=4) {
  let start=number(entity.start),end=entity.end==null?180:number(entity.end);
  while(end<start)end+=360;
  const span=Math.min(360,Math.max(0,end-start));
  const steps=Math.max(2,Math.ceil(span/maximumStepDegrees));
  return Array.from({length:steps+1},(_,index)=>{
    const angle=(start+span*index/steps)*Math.PI/180;
    return {x:number(entity.cx)+Math.cos(angle)*number(entity.r),y:number(entity.cy)+Math.sin(angle)*number(entity.r)};
  });
}

export function entityPathPoints(entity={}) {
  if(entity.type==='line')return [{x:number(entity.x1),y:number(entity.y1)},{x:number(entity.x2),y:number(entity.y2)}];
  if(entity.type==='arc')return arcPoints(entity);
  if(entity.type==='polyline')return (entity.points||[]).map(point=>({x:number(point.x),y:number(point.y)}));
  return [];
}

function circlePoints(entity) {
  return arcPoints({...entity,start:0,end:360},4).slice(0,-1);
}

function endpointKey(point,tolerance) {
  return `${Math.round(number(point.x)/tolerance)}:${Math.round(number(point.y)/tolerance)}`;
}

export function reconstructPreviewLoops(entities=[],toleranceMm=.35) {
  const tolerance=Math.max(.000001,number(toleranceMm));
  const loops=[];
  const segments=[];
  entities.forEach((entity,index)=>{
    if(entityRole(entity)!=='cut')return;
    if(entity.type==='circle'&&number(entity.r)>0){loops.push({points:circlePoints(entity),entityIndexes:[index],reconstructed:false});return;}
    const points=entityPathPoints(entity);
    if(entity.type==='polyline'&&entity.closed&&points.length>=3){loops.push({points,entityIndexes:[index],reconstructed:false});return;}
    if(points.length<2)return;
    const first=endpointKey(points[0],tolerance),last=endpointKey(points[points.length-1],tolerance);
    if(first!==last)segments.push({entityIndex:index,points,first,last});
  });
  const adjacency=new Map();
  const add=(key,index)=>adjacency.set(key,[...(adjacency.get(key)||[]),index]);
  segments.forEach((segment,index)=>{add(segment.first,index);add(segment.last,index);});
  const pending=new Set(segments.map((_,index)=>index));
  const unresolved=[];
  while(pending.size){
    const seed=pending.values().next().value,component=new Set(),queue=[seed];
    while(queue.length){const index=queue.pop();if(component.has(index))continue;component.add(index);const segment=segments[index];queue.push(...(adjacency.get(segment.first)||[]),...(adjacency.get(segment.last)||[]));}
    component.forEach(index=>pending.delete(index));
    const nodes=new Set([...component].flatMap(index=>[segments[index].first,segments[index].last]));
    if(component.size<2||[...nodes].some(node=>(adjacency.get(node)||[]).filter(index=>component.has(index)).length!==2)){unresolved.push(...[...component].map(index=>segments[index].entityIndex));continue;}
    const firstIndex=component.values().next().value,firstSegment=segments[firstIndex];
    const start=firstSegment.first;let current=firstSegment.last,ordered=[...firstSegment.points],used=new Set([firstIndex]);
    while(used.size<component.size){
      const candidates=(adjacency.get(current)||[]).filter(index=>component.has(index)&&!used.has(index));
      if(candidates.length!==1)break;
      const nextIndex=candidates[0],segment=segments[nextIndex];
      const forward=segment.first===current,path=forward?segment.points:[...segment.points].reverse();
      current=forward?segment.last:segment.first;ordered.push(...path.slice(1));used.add(nextIndex);
    }
    if(used.size!==component.size||current!==start){unresolved.push(...[...component].map(index=>segments[index].entityIndex));continue;}
    const first=ordered[0],last=ordered[ordered.length-1];
    if(Math.hypot(first.x-last.x,first.y-last.y)<=tolerance)ordered=ordered.slice(0,-1);
    loops.push({points:ordered,entityIndexes:[...component].map(index=>segments[index].entityIndex),reconstructed:true});
  }
  return {loops,unresolvedEntityIndexes:[...new Set(unresolved)].sort((a,b)=>a-b)};
}

export function previewFillPath(geometry={}) {
  const {loops,unresolvedEntityIndexes}=reconstructPreviewLoops(geometry.entities||[]);
  const path=loops.filter(loop=>loop.points.length>=3).map(loop=>`M ${loop.points.map(point=>`${point.x} ${point.y}`).join(' L ')} Z`).join(' ');
  return {path,loopCount:loops.length,unresolvedEntityIndexes};
}

export function previewBounds(geometry={},autoOrient=false) {
  const bounds=geometry.bounds||{};
  const minX=number(bounds.minX),minY=number(bounds.minY),maxX=number(bounds.maxX??geometry.widthMm??1),maxY=number(bounds.maxY??geometry.heightMm??1);
  const width=Math.max(.001,maxX-minX),height=Math.max(.001,maxY-minY);
  const padX=Math.max(width*.075,Math.min(width,height)*.12,.5);
  const padY=Math.max(height*.075,Math.min(width,height)*.12,.5);
  const rotationDeg=autoOrient&&height>width*1.5?-90:0;
  const cx=(minX+maxX)/2,cy=(minY+maxY)/2;
  const viewBox=rotationDeg?`${cx-(height+2*padY)/2} ${cy-(width+2*padX)/2} ${height+2*padY} ${width+2*padX}`:`${minX-padX} ${minY-padY} ${width+2*padX} ${height+2*padY}`;
  return {minX,minY,maxX,maxY,width,height,padX,padY,rotationDeg,viewBox};
}
